import React, { useMemo } from 'react';
import css from './TrainerDashboard.module.scss';
import {
  CheckCircle,
  PlusCircle,
  BookOpen,
  FileText,
  Users,
  Activity,
  Mail,
  MessageSquare,
  TrendingUp,
  TrendingDown,
  Minus,
  Eye,
  ChevronLeft,
  ChevronRight,
  ClipboardCheck,
  Clock
} from 'lucide-react';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip } from 'recharts';
import HeroBanner from './HeroBanner';
import { Link } from 'react-router-dom';

const mockLearners = [
  { id: 'L001', name: 'Julian Vance', email: 'j.vance@university.edu', initials: 'JV', course: 'XI-A', lastActive: '4 days ago', progress: 32, status: 'AT RISK' },
  { id: 'L002', name: 'Elena Martinez', email: 'e.martinez@university.edu', initials: 'EM', course: 'X-F', lastActive: '2 hours ago', progress: 64, status: 'STALLING' },
  // { id: 'L003', name: 'Marcus Thorne', email: 'm.thorne@university.edu', initials: 'MT', course: 'Bio-Organic Chemistry', lastActive: '10 mins ago', progress: 88, status: 'ON TRACK' },
  { id: 'L004', name: 'Sarah Lindholm', email: 's.lindholm@university.edu', initials: 'SL', course: 'XII-B', lastActive: '1 week ago', progress: 15, status: 'BEHIND' },
  { id: 'L005', name: 'Thomas Chen', email: 't.chen@university.edu', initials: 'TC', course: 'XI-C', lastActive: '3 days ago', progress: 48, status: 'STALLING' },
];

const classOverviewData = [
  { name: 'Completed', value: 70, color: '#10b981' },
  { name: 'In Progress', value: 20, color: '#3b82f6' },
  { name: 'Yet To Start', value: 10, color: '#f59e0b' },
];

const TrainerDashboard = ({ name = "Arjun", userDetails, weeklyUserTime, trainerCardData, addRedirection }) => {
  const totalWeeklyHours = useMemo(() => {
    if (!weeklyUserTime || !Array.isArray(weeklyUserTime)) return 0;
    return weeklyUserTime.reduce((acc, curr) => acc + (curr.total_duration || 0), 0);
  }, [weeklyUserTime]);

  const formattedHours = Math.floor(totalWeeklyHours);
  const formattedMinutes = Math.round((totalWeeklyHours - formattedHours) * 60);

  const renderProgressBar = (value) => {
    let colorClass = css.progressGood;
    if (value < 40) colorClass = css.progressPoor;
    else if (value < 75) colorClass = css.progressAverage;

    return (
      <div className={css.progressCell}>
        <div className={css.progressBarBg}>
          <div className={`${css.progressBarFill} ${colorClass}`} style={{ width: `${value}%` }}></div>
        </div>
        <span className={`${css.progressText} ${colorClass}`}>{value}%</span>
      </div>
    );
  };

  const getLearnerStatusTag = (status) => {
    if (status === 'AT RISK') return <span className={`${css.statusPill} ${css.pillRisk}`}>AT RISK</span>;
    if (status === 'STALLING') return <span className={`${css.statusPill} ${css.pillStalling}`}>STALLING</span>;
    if (status === 'BEHIND') return <span className={`${css.statusPill} ${css.pillRisk}`}>BEHIND</span>;
    return null;
  };

  return (
    <div className={css.dashboardContainer}>
      {/* Hero Banner Component */}
      <HeroBanner name={name} addRedirection={addRedirection} />

      {/* Metrics Grid */}
      <div className={css.metricsGrid}>
        <div className={css.animatedCard} style={{ backgroundColor: 'white', border: '1px solid #e5e7eb', borderRadius: '10px', padding: '12px', display: 'flex', flexDirection: 'column', justifyContent: 'space-between', boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)' }}>
          <span style={{ fontSize: '14px', color: '#0c0c0c', fontWeight: '600' }}>My Courses</span>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', marginTop: '8px' }}>
            <div>
              <div style={{ fontSize: '28px', fontWeight: '600', color: '#0f172a', lineHeight: 1.2 }}>{trainerCardData?.myclasses || 0}</div>
              <div style={{ fontSize: '11px', color: '#64748b', marginTop: '4px' }}>+2 this month</div>
            </div>
            <BookOpen size={24} color="#ea580c" strokeWidth={2} />
          </div>
        </div>

        <div className={css.animatedCard} style={{ backgroundColor: 'white', border: '1px solid #e5e7eb', borderRadius: '10px', padding: '12px', display: 'flex', flexDirection: 'column', justifyContent: 'space-between', boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)' }}>
          <span style={{ fontSize: '14px', color: '#060606', fontWeight: '600' }}>Active Assessments</span>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', marginTop: '8px' }}>
            <div>
              <div style={{ fontSize: '28px', fontWeight: '600', color: '#0f172a', lineHeight: 1.2 }}>{trainerCardData?.activeass || 0}</div>
              <div style={{ fontSize: '11px', color: '#64748b', marginTop: '4px' }}>3 closing soon</div>
            </div>
            <ClipboardCheck size={24} color="#059669" strokeWidth={2} />
          </div>
        </div>

        <div className={`${css.metricCard} ${css.animatedCard}`} style={{ padding: '12px', borderRadius: '10px' }}>
          <div className={css.cardHeader} style={{ marginBottom: '8px' }}>
            <span style={{ fontSize: '14px', color: '#060606', fontWeight: '600' }}>Learner Distribution</span>
          </div>

          <div className={css.cardBody}>
            <div className={css.statRow}>
              <span className={css.label} style={{ fontSize: '11px' }}>Total Learners</span>
              <span className={css.value} style={{ fontSize: '11px' }}></span>
            </div>
            <div className={css.progressBar} style={{ height: '8px', marginTop: '6px' }}>
              <div className={`${css.segment} ${css.success}`} style={{ width: '60%' }} title="On Track: 60%"></div>
              <div className={`${css.segment} ${css.warning}`} style={{ width: '25%' }} title="At Risk: 25%"></div>
              <div className={`${css.segment} ${css.danger}`} style={{ width: '15%' }} title="Behind: 15%"></div>
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'flex-end', marginTop: '8px' }}>
              <Users size={24} color="#eab308" strokeWidth={2} />
            </div>
          </div>
        </div>

        <div className={css.animatedCard} style={{ backgroundColor: 'white', border: '1px solid #e5e7eb', borderRadius: '10px', padding: '12px', display: 'flex', flexDirection: 'column', justifyContent: 'space-between', boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)' }}>
          <span style={{ fontSize: '14px', color: '#000000', fontWeight: '600' }}>Weekly Activity Time</span>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', marginTop: '8px' }}>
            <div>
              <div style={{ fontSize: '24px', fontWeight: '600', color: '#0f172a', lineHeight: 1.2 }}>{formattedHours}h {formattedMinutes}m</div>
              <div style={{ fontSize: '11px', color: '#64748b', marginTop: '4px' }}>+4h vs last week</div>
            </div>
            <Clock size={24} color="#f59e0b" strokeWidth={2} />
          </div>
        </div>
      </div>

      {/* Middle Grid */}
      <div className={css.middleGrid}>
        <div className={css.sectionCard}>
          <div className={css.sectionHeader}>
            <h2>Class Overview</h2>
          </div>
          <div className={css.chartContainer}>
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={classOverviewData}
                  innerRadius={60}
                  outerRadius={80}
                  paddingAngle={5}
                  dataKey="value"
                >
                  {classOverviewData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          </div>
          <div className={css.progressLegend} style={{ justifyContent: 'center' }}>
            <div className={css.legendItem}><span className={`${css.dot} ${css.success}`}></span> Completed</div>
            <div className={css.legendItem}><span className={`${css.dot} ${css.primary}`} style={{ backgroundColor: '#3b82f6' }}></span> In Progress</div>
            <div className={css.legendItem}><span className={`${css.dot} ${css.warning}`}></span> Yet To Start</div>
          </div>
        </div>

        <div className={css.sectionCard}>
          <div className={css.sectionHeader}>
            <h2>Recent Assignments</h2>
            <span className={css.viewAll}>View All</span>
          </div>
          <div className={css.assignmentList}>
            <div className={css.assignmentItem}>
              <div className={css.itemInfo}>
                <h4>React Basics Quiz</h4>
                <p>Advanced React • 45/45 Submitted</p>
              </div>
              <span className={`${css.tag} ${css.success}`}>Reviewed</span>
            </div>
            <div className={css.assignmentItem}>
              <div className={css.itemInfo}>
                <h4>Node API Project</h4>
                <p>Node.js Backend • 28/30 Submitted</p>
              </div>
              <span className={`${css.tag} ${css.warning}`}>Pending Review</span>
            </div>
            <div className={css.assignmentItem}>
              <div className={css.itemInfo}>
                <h4>UI Wireframes</h4>
                <p>UI/UX Design • 0/25 Submitted</p>
              </div>
              <span className={`${css.tag} ${css.neutral}`}>Draft</span>
            </div>
          </div>
        </div>

        <div className={css.sectionCard}>
          <div className={css.sectionHeader}>
            <h2>Today's Schedule</h2>
            <Link to="/time-table" className={css.viewAll}>View Full Timetable</Link>
          </div>
          <div className={css.timelineList}>
            <div className={css.timelineItem}>
              <div className={css.timeCol}>
                <span className={css.time}>08:00 AM</span>
                <span className={css.duration}>45 min</span>
              </div>
              <div className={css.track}>
                <span className={`${css.node} ${css.completed}`}></span>
                <div className={css.line}></div>
              </div>
              <div className={css.contentCol}>
                <h4 className={css.title}>Chemistry - Grade 11B</h4>
                <p className={css.subtitle}>Chapter 4: Chemical Bonding</p>
              </div>
              <div className={css.badgeCol}>
                <span className={`${css.statusBadge} ${css.completedBadge}`}>Completed</span>
              </div>
            </div>

            <div className={css.timelineItem}>
              <div className={css.timeCol}>
                <span className={css.time}>09:00 AM</span>
                <span className={css.duration}>45 min</span>
              </div>
              <div className={css.track}>
                <span className={`${css.node} ${css.completed}`}></span>
                <div className={css.line}></div>
              </div>
              <div className={css.contentCol}>
                <h4 className={css.title}>Mathematics - Grade 9A</h4>
                <p className={css.subtitle}>Algebra Basics</p>
              </div>
              <div className={css.badgeCol}>
                <span className={`${css.statusBadge} ${css.completedBadge}`}>Completed</span>
              </div>
            </div>

            <div className={css.timelineItem}>
              <div className={css.timeCol}>
                <span className={css.time}>10:30 AM</span>
                <span className={css.duration}>60 min</span>
              </div>
              <div className={css.track}>
                <span className={`${css.node} ${css.live}`}></span>
                <div className={css.line}></div>
              </div>
              <div className={css.contentCol}>
                <h4 className={css.title}>Physics - Grade 11A</h4>
                <p className={css.subtitle}>Work, Energy & Power</p>
              </div>
              <div className={css.badgeCol}>
                <span className={`${css.statusBadge} ${css.liveBadge}`}>Live Now</span>
              </div>
            </div>

            <div className={css.timelineItem}>
              <div className={css.timeCol}>
                <span className={css.time}>01:00 PM</span>
                <span className={css.duration}>45 min</span>
              </div>
              <div className={css.track}>
                <span className={`${css.node} ${css.upcoming}`}></span>
                <div className={css.line}></div>
              </div>
              <div className={css.contentCol}>
                <h4 className={css.title}>Physics - Grade 10B</h4>
                <p className={css.subtitle}>Newton's Laws of Motion</p>
              </div>
              <div className={css.badgeCol}>
                <span className={`${css.statusBadge} ${css.upcomingBadge}`}>Upcoming</span>
              </div>
            </div>

            <div className={css.timelineItem}>
              <div className={css.timeCol}>
                <span className={css.time}>02:00 PM</span>
                <span className={css.duration}>45 min</span>
              </div>
              <div className={css.track}>
                <span className={`${css.node} ${css.upcoming}`}></span>
                <div className={css.line}></div>
              </div>
              <div className={css.contentCol}>
                <h4 className={css.title}>Doubt Clearing Session</h4>
                <p className={css.subtitle}>Open for All Students</p>
              </div>
              <div className={css.badgeCol}>
                <span className={`${css.statusBadge} ${css.upcomingBadge}`}>Upcoming</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom Table */}
      <div className={css.learnerTableSection}>
        <div style={{ overflowX: 'auto' }}>
          <table className={css.learnerTable}>
            <thead>
              <tr>
                <th>LEARNER</th>
                <th>CLASS</th>
                <th>LAST ACTIVE</th>
                <th>PROGRESS</th>
                <th>STATUS</th>
                {/* <th>ACTIONS</th> */}
              </tr>
            </thead>
            <tbody>
              {mockLearners.map((learner) => (
                <tr key={learner.id}>
                  <td>
                    <div className={css.learnerCell}>
                      {learner.avatar ? (
                        <img src={learner.avatar} alt={learner.name} className={css.avatarImg} />
                      ) : (
                        <div className={css.avatarInitials}>{learner.initials}</div>
                      )}
                      <div>
                        <div className={css.learnerName}>{learner.name}</div>
                        <div className={css.learnerEmail}>{learner.email}</div>
                      </div>
                    </div>
                  </td>
                  <td>
                    <div className={css.courseName}>{learner.course}</div>
                  </td>
                  <td>
                    <div className={css.lastActive}>{learner.lastActive}</div>
                  </td>
                  <td>{renderProgressBar(learner.progress)}</td>
                  <td>{getLearnerStatusTag(learner.status)}</td>
                  <td>
                    {/* <div className={css.actionIcons}>
                      <button title="View"><Eye size={18} /></button>
                      <button title="Message"><Mail size={18} /></button>
                    </div> */}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className={css.tableFooter}>
          <div className={css.showingText}>Showing 1 to 5 of 123 learners</div>
          <div className={css.pagination}>
            <button className={css.pageBtn}><ChevronLeft size={16} /></button>
            <button className={`${css.pageBtn} ${css.active}`}>1</button>
            <button className={css.pageBtn}>2</button>
            <button className={css.pageBtn}>3</button>
            <span className={css.dots}>...</span>
            <button className={css.pageBtn}>25</button>
            <button className={css.pageBtn}><ChevronRight size={16} /></button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TrainerDashboard;
